import { useQuery } from '@apollo/client';
import { Devices, Event, PlayCircleOutlined, Timelapse } from '@mui/icons-material';
import { Box, Grid, Typography } from '@mui/material';
import type { GridColDef, GridRowParams } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { DataGridPagination } from '~/components/data-grid';
import { LoadingPane } from '~/components/loading-pane';
import { AssetLink } from '../components/AssetLink';
import { ByEventCharts } from '../components/ByEventCharts';
import { PdfExportControls } from '../components/PdfExportControls';
import { ReportDataGrid } from '../components/ReportDataGrid';
import { StatCard } from '../components/StatCard';
import { ZoneAndDeviceGroupDonuts } from '../components/ZoneAndDeviceGroupDonuts';
import {
  CardPaper,
  EMPTY_STATS,
  SectionStack,
  TabRoot,
  avgPerEventPerDevice,
  formatMinutes,
  useEventMetricColumns,
} from '../lib';
import { PlayoutReportAssetDetailDocument } from '../queries/PlayoutReportAssetDetail.generated';
import type { PlayoutReport__SponsorSummary as SponsorRow } from '../queries/PlayoutReportBySponsor.generated';
import { usePlayoutReport } from './context';

export const PlayoutReportsByAssetDetail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { search } = useLocation();
  const { kind, assetId: assetIdParam } = useParams<{ kind: string; assetId: string }>();
  const assetId = Number(assetIdParam);

  const { variables, querySkipped } = usePlayoutReport();

  const { data, loading } = useQuery(PlayoutReportAssetDetailDocument, {
    skip: querySkipped || !Number.isFinite(assetId) || !kind,
    fetchPolicy: 'network-only',
    variables: { ...variables, assetId, kind: kind ?? '' },
  });

  const detail = data?.network?.playoutReport.assetDetail;
  const stats = detail?.stats ?? EMPTY_STATS;
  const assetName = detail?.assetName ?? '';
  const byEvent = detail?.byEvent ?? [];
  const byZone = detail?.byZone ?? [];
  const byDeviceGroup = detail?.byDeviceGroup ?? [];
  const thumbnailUri = detail?.thumbnailUri ?? null;

  const uniqueDevices = detail ? detail.stats.uniqueDevices : null;

  const avgPlaysPerDevice = avgPerEventPerDevice(stats.totalPlays, stats.eventCount, uniqueDevices);
  const avgDurationPerDevice = avgPerEventPerDevice(
    stats.totalDurationMs,
    stats.eventCount,
    uniqueDevices,
  );

  const sponsorRows = detail?.associatedSponsors ?? [];

  const eventMetricColumns = useEventMetricColumns<SponsorRow>({ averages: false });

  const sponsorColumns = useMemo<GridColDef<SponsorRow>[]>(
    () => [
      {
        field: 'sponsor',
        headerName: t('pages.playout_reports.top_assets.columns.sponsor'),
        flex: 1.5,
        minWidth: 180,
        hideable: false,
      },
      ...eventMetricColumns,
    ],
    [t, eventMetricColumns],
  );

  return (
    <TabRoot>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography sx={{ fontSize: 24, fontWeight: 600, lineHeight: 1.2 }}>
          {detail && kind ? (
            <AssetLink
              kind={kind}
              mediaItemId={kind === 'sponsor' && Number.isFinite(assetId) ? assetId : null}
              contentItemId={kind !== 'sponsor' && Number.isFinite(assetId) ? assetId : null}
              contentFolderId={detail.contentFolderId ?? null}
              label={assetName || t('pages.playout_reports.by_asset.detail.unknown_asset')}
            />
          ) : (
            assetName || t('pages.playout_reports.by_asset.detail.unknown_asset')
          )}
        </Typography>
        {kind && Number.isFinite(assetId) && (
          <PdfExportControls
            identifier={{ asset: { assetId, kind } }}
            name={assetName || t('pages.playout_reports.by_asset.detail.unknown_asset')}
            eventCount={byEvent.length}
            hasTableRows={sponsorRows.length > 0}
            hasThumbnail={thumbnailUri != null}
          />
        )}
      </Box>

      <LoadingPane in={loading && !data} size={60} thickness={4} delay="0ms">
        {data && (
          <SectionStack>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  icon={<PlayCircleOutlined />}
                  label={t('pages.playout_reports.stats.total_plays.label')}
                  value={
                    avgPlaysPerDevice != null
                      ? avgPlaysPerDevice.toLocaleString(undefined, { maximumFractionDigits: 1 })
                      : '—'
                  }
                  subtitle={t('pages.playout_reports.stats.total_plays.subtitle', {
                    total: stats.totalPlays.toLocaleString(),
                  })}
                  tooltip={t('pages.playout_reports.stats.total_plays.tooltip')}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  icon={<Timelapse />}
                  label={t('pages.playout_reports.stats.total_airtime.label')}
                  value={
                    avgDurationPerDevice != null
                      ? `${formatMinutes(avgDurationPerDevice).toLocaleString()} m`
                      : '—'
                  }
                  subtitle={t('pages.playout_reports.stats.total_airtime.subtitle', {
                    total: formatMinutes(stats.totalDurationMs).toLocaleString(),
                    hours: Math.round(stats.totalDurationMs / 3_600_000).toLocaleString(),
                  })}
                  tooltip={t('pages.playout_reports.stats.total_airtime.tooltip')}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  icon={<Event />}
                  label={t('pages.playout_reports.by_asset.detail.events_count.label')}
                  value={stats.eventCount.toLocaleString()}
                  tooltip={t('pages.playout_reports.by_asset.detail.events_count.tooltip')}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  icon={<Devices />}
                  label={t('pages.playout_reports.stats.devices_reached.label')}
                  value={uniqueDevices != null ? uniqueDevices.toLocaleString() : '—'}
                  subtitle={t('pages.playout_reports.stats.devices_reached.across_groups', {
                    count: stats.uniqueDeviceGroups,
                    groups: t('common.pluralize.device_group_plural', {
                      count: stats.uniqueDeviceGroups,
                    }),
                  })}
                  tooltip={t('pages.playout_reports.stats.devices_reached.tooltip')}
                />
              </Grid>
            </Grid>

            <ByEventCharts events={byEvent} />

            <ZoneAndDeviceGroupDonuts
              byZone={byZone}
              byDeviceGroup={byDeviceGroup}
              totalDurationMs={stats.totalDurationMs}
            />

            {sponsorRows.length > 0 && (
              <CardPaper sx={{ pb: 0 }}>
                <Typography component="h2" variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                  {t('pages.playout_reports.by_asset.detail.associated_sponsors')}
                </Typography>
                <ReportDataGrid
                  columnStateKey="assetDetailSponsors"
                  columns={sponsorColumns}
                  rows={sponsorRows}
                  disableRowSelectionOnClick
                  getRowId={(r: SponsorRow) => r.sponsorId}
                  onRowClick={({ row }: GridRowParams<SponsorRow>) =>
                    navigate({ pathname: `../by-sponsor/${row.sponsorId}`, search })
                  }
                  pageSizeOptions={[10, 25, 50]}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 10, page: 0 } },
                    sorting: { sortModel: [{ field: 'durationMs', sort: 'desc' }] },
                  }}
                  slots={{ pagination: DataGridPagination }}
                />
              </CardPaper>
            )}

            {thumbnailUri && (
              <CardPaper>
                <Typography component="h2" variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                  {t('pages.playout_reports.by_asset.detail.thumbnail')}
                </Typography>
                <Box
                  component="img"
                  src={thumbnailUri}
                  crossOrigin="anonymous"
                  alt={assetName}
                  sx={{
                    display: 'block',
                    maxWidth: '100%',
                    maxHeight: 320,
                    objectFit: 'contain',
                    borderRadius: 1,
                  }}
                />
              </CardPaper>
            )}
          </SectionStack>
        )}
      </LoadingPane>
    </TabRoot>
  );
};
